import React, {useState, useRef, useEffect} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {FormattedMessage} from 'react-intl';

import AppStateHOC from '../lib/app-state-hoc.jsx';
import GUI from '../containers/gui.jsx';
import HashParserHOC from '../lib/hash-parser-hoc.jsx';
import log from '../lib/log.js';
import MessageBoxType from '../lib/message-box.js';
import showAppDialog from '../lib/app-dialog-service.js';
import HomeScreen from '../components/home-screen/home-screen.jsx';

/* Mirrors desktop's app.jsx mode switcher: a Home screen that picks Blocks / Robotics /
   AI & ML. The editor is NOT mounted until the user first picks a mode that needs it —
   while hidden via display:none its container is 0×0, which stalls the stage/VM/Blockly
   sizing logic forever (desktop hit this same issue; see its app.jsx comment). Once
   mounted it's kept alive and only hidden (not unmounted) on later trips back home, so
   Blockly/VM state survives.

   Robotics and ML already have a window-event path into the gui package (menu-bar.jsx),
   but on FIRST mount the editor's own HOC chain (font/locale loading, VM init, default
   project load) is asynchronous, so menu-bar's listener isn't registered yet on the very
   next tick — dispatching immediately after setGuiMounted(true) loses the event. The
   container's onProjectLoaded callback fires once the default project has genuinely
   finished loading (Redux-driven, not a guessed delay), which is also exactly when
   menu-bar has mounted — so the pending event is held until then. On later visits the
   editor is already mounted and ready, so it dispatches immediately. */
const PlaygroundApp = ({guiElement}) => {
    const [mode, setMode] = useState('home'); // 'home' | 'editor'
    const [guiMounted, setGuiMounted] = useState(false);
    const guiReadyRef = useRef(false);
    const pendingEventRef = useRef(null);

    const dispatchPendingEvent = () => {
        if (!pendingEventRef.current) return;
        const eventName = pendingEventRef.current;
        pendingEventRef.current = null;
        window.dispatchEvent(new CustomEvent(eventName));
    };

    // Fires once, the first time the default project finishes loading. Deferred to the
    // next tick: project-fetcher-hoc.jsx ALSO reacts to this same "project loaded"
    // transition by force-activating the Blocks tab (its normal, intended behavior for
    // opening a project) in the same React commit, one componentDidUpdate after this one
    // — dispatching synchronously here would just get overwritten by that reset a moment
    // later. Firing after a setTimeout(0) lets that reset finish first, so ours applies last.
    const handleProjectLoaded = () => {
        if (guiReadyRef.current) return;
        guiReadyRef.current = true;
        setTimeout(dispatchPendingEvent, 0);
    };

    // On later visits the editor is already mounted/ready (onProjectLoaded won't fire
    // again for an already-loaded project), so dispatch immediately here instead. Also
    // re-dispatches a resize so Blockly/flyout re-measure after display:none → block
    // (same fix desktop's app.jsx uses).
    useEffect(() => {
        if (!guiMounted || mode !== 'editor') return;
        if (guiReadyRef.current) dispatchPendingEvent();
        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        });
        return () => cancelAnimationFrame(raf1);
    }, [guiMounted, mode]);

    const handleSelectMode = modeId => {
        if (modeId === 'robotics') {
            pendingEventRef.current = 'robocoders:open-robotics';
        } else if (modeId === 'ml') {
            pendingEventRef.current = 'robocoders:open-ml';
        }
        setGuiMounted(true);
        setMode('editor');
    };

    return (
        <React.Fragment>
            {mode === 'home' && <HomeScreen onSelectMode={handleSelectMode} />}
            {guiMounted && (
                <div style={{display: mode === 'editor' ? 'block' : 'none', width: '100%', height: '100%'}}>
                    {React.cloneElement(guiElement, {
                        onGoHome: () => setMode('home'),
                        onProjectLoaded: handleProjectLoaded
                    })}
                </div>
            )}
        </React.Fragment>
    );
};

PlaygroundApp.propTypes = {
    guiElement: PropTypes.node.isRequired
};

const onClickCheckUpdate = () => {
    log('User click check update');
};

const onClickUpdate = () => {
    log('User click update');
};

const onAbortUpdate = () => {
    log('User click abort update');
};

const onClickClearCache = () => {
    log('User click clear cahce');
};

const onClickInstallDriver = () => {
    log('User click install driver');
};

const handleTelemetryModalCancel = () => {
    log('User canceled telemetry modal');
};

const handleTelemetryModalOptIn = () => {
    log('User opted into telemetry');
};

const handleTelemetryModalOptOut = () => {
    log('User opted out of telemetry');
};

const onClickAbout = [
    {
        title: (<FormattedMessage
            defaultMessage="About"
            description="Menu bar item for about"
            id="gui.desktopMenuBar.about"
        />),
        onClick: () => log('About')
    },
    {
        title: (<FormattedMessage
            defaultMessage="License"
            description="Menu bar item for license"
            id="gui.desktopMenuBar.license"
        />),
        onClick: () => log('License')
    },
    {
        title: (<FormattedMessage
            defaultMessage="Privacy policy"
            description="Menu bar item for privacy policy"
            id="gui.menuBar.privacyPolicy"
        />),
        onClick: () => log('Privacy Policy')
    },
    {
        title: (<FormattedMessage
            defaultMessage="Data settings"
            description="Menu bar item for data settings"
            id="gui.menuBar.dataSettings"
        />),
        onClick: () => log('Data Settings')
    }
];

const handleShowMessageBox = (type, message) => {
    if (type === MessageBoxType.confirm) {
        return showAppDialog({
            type: 'question',
            title: 'Confirm',
            message,
            buttons: ['OK', 'Cancel'],
            defaultId: 0
        }).then(idx => idx === 0);
    }
    return showAppDialog({
        type: 'info',
        title: 'Notice',
        message,
        buttons: ['OK'],
        defaultId: 0
    });
};

/*
 * Render the GUI playground. This is a separate function because importing anything
 * that instantiates the VM causes unsupported browsers to crash
 * {object} appTarget - the DOM element to render to
 */
export default appTarget => {
    GUI.setAppElement(appTarget);

    // note that redux's 'compose' function is just being used as a general utility to make
    // the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
    // ability to compose reducers.
    const WrappedGui = compose(
        AppStateHOC,
        HashParserHOC
    )(GUI);

    // TODO a hack for testing the backpack, allow backpack host to be set by url param
    const backpackHostMatches = window.location.href.match(/[?&]backpack_host=([^&]*)&?/);
    const backpackHost = backpackHostMatches ? backpackHostMatches[1] : null;

    const scratchDesktopMatches = window.location.href.match(/[?&]isScratchDesktop=([^&]+)/);
    let simulateScratchDesktop;
    if (scratchDesktopMatches) {
        try {
            // parse 'true' into `true`, 'false' into `false`, etc.
            simulateScratchDesktop = JSON.parse(scratchDesktopMatches[1]);
        } catch {
            // it's not JSON so just use the string
            // note that a typo like "falsy" will be treated as true
            simulateScratchDesktop = scratchDesktopMatches[1];
        }
    }

    if (process.env.NODE_ENV === 'production' && typeof window === 'object') {
        // Warn before navigating away
        window.onbeforeunload = () => true;
    }

    // important: this is checking whether `simulateScratchDesktop` is truthy, not just defined!
    const guiElement = simulateScratchDesktop ?
        (<WrappedGui
            canEditTitle
            isScratchDesktop
            onClickAbout={onClickAbout}
            showTelemetryModal
            canSave={false}
            onTelemetryModalCancel={handleTelemetryModalCancel}
            onTelemetryModalOptIn={handleTelemetryModalOptIn}
            onTelemetryModalOptOut={handleTelemetryModalOptOut}
            onAbortUpdate={onAbortUpdate}
            onClickCheckUpdate={onClickCheckUpdate}
            onClickUpdate={onClickUpdate}
            onClickClearCache={onClickClearCache}
            onClickInstallDriver={onClickInstallDriver}
            onShowMessageBox={handleShowMessageBox}
        />) :
        (<WrappedGui
            canEditTitle
            backpackVisible
            showComingSoon
            backpackHost={backpackHost}
            canSave={false}
            onShowMessageBox={handleShowMessageBox}
        />);

    ReactDOM.render(<PlaygroundApp guiElement={guiElement} />, appTarget);
};
